import { UserSettings } from '../../enums/user-settings/user-settings';
import { UserSettingsOptions } from '../../enums/user-settings/user-settings-options';

import type { MessageDescriptor } from 'react-intl';

export type UserSettingOption = {
  settingValue: string | boolean;
  // Optional because `user-settings-options.js` attaches it at runtime, to the labelled options only.
  messageDescriptor?: MessageDescriptor;
};

export type LabelledUserSettingOption = UserSettingOption & { messageDescriptor: MessageDescriptor };

/** Anything with a `key` addresses the map; in practice these are the `UserSettings` entries. */
export type UserSettingKey = {
  key: string;
};

const trueFalse: UserSettingOption[] = [{ settingValue: true }, { settingValue: false }];
// hideCustomerSummaryPopover returns "false" as the show value from the BE and this avoids a bunch of bangs in the UI.
const falseTrue: UserSettingOption[] = [{ settingValue: false }, { settingValue: true }];

export const userSettingsOptionsMap: Record<string, UserSettingOption[]> = {
  [UserSettings.defaultButtonAction.key]: [UserSettingsOptions.createLabel, UserSettingsOptions.createAndPrintLabel],
  [UserSettings.defaultUrl.key]: [
    UserSettingsOptions.loginToInsightsOverview,
    UserSettingsOptions.loginToInsightsOperations,
    UserSettingsOptions.loginToInsightsSalesTrends,
    UserSettingsOptions.loginToInsightsCustomerOverview,
    UserSettingsOptions.loginToInsightsProductHighlights,
    UserSettingsOptions.loginToOrders,
    UserSettingsOptions.loginToShipments,
    UserSettingsOptions.loginToProducts,
    UserSettingsOptions.loginToCustomers,
  ],
  [UserSettings.verifyItemsBy.key]: [UserSettingsOptions.upc, UserSettingsOptions.itemSku, UserSettingsOptions.fulfillmentSku],
  [UserSettings.requiredManualConfirmation.key]: [
    UserSettingsOptions.makeUsersClickMarkAsVerified,
    UserSettingsOptions.automaticallyPrintAfterVerified,
  ],
  [UserSettings.uiTimeFormat.key]: [UserSettingsOptions.twelveHour, UserSettingsOptions.twentyFourHour],
  [UserSettings.uiDateFormat.key]: [
    UserSettingsOptions.dayMonthYearLong,
    UserSettingsOptions.dayMonthYearShort,
    UserSettingsOptions.monthDayYearLong,
    UserSettingsOptions.monthDayYearShort,
  ],
  [UserSettings.advancedHotkeys.key]: trueFalse,
  [UserSettings.weeklyDigest.key]: trueFalse,
  [UserSettings.isQuickshipEnabled.key]: trueFalse,
  [UserSettings.quickshipShowCostSummary.key]: [
    UserSettingsOptions.quickshipAlwaysShowCostSummary,
    UserSettingsOptions.quickshipShowCostSummaryIfNeeded,
    UserSettingsOptions.quickshipNeverShowCostSummary,
  ],
  [UserSettings.quickshipShowErrors.key]: trueFalse,
  [UserSettings.quickshipShowLabelBatchStatus.key]: trueFalse,
  [UserSettings.quickshipShowPrintDialogPreview.key]: trueFalse,
  [UserSettings.quickshipShowWarnings.key]: trueFalse,
  [UserSettings.hideCustomerSummaryPopover.key]: falseTrue,
  [UserSettings.hideExternallyChangedOrders.key]: trueFalse,
  [UserSettings.shouldPersistCombineOrderSelection.key]: falseTrue,
  [UserSettings.uspsNsaCopOnboardingCompleted.key]: trueFalse,
  [UserSettings.uspsNsaWaitingBannerDismissed.key]: trueFalse,
  [UserSettings.phoneNumberCaptureModalDismissed.key]: trueFalse,
  [UserSettings.phoneNumberCaptureBannerDismissed.key]: trueFalse,
  [UserSettings.planMigrationModalDismissed.key]: trueFalse,
};

export const getUserSettingsOptions = (userSetting: UserSettingKey): UserSettingOption[] => {
  return userSettingsOptionsMap[userSetting.key];
};

const isLabelledOption = (option: UserSettingOption): option is LabelledUserSettingOption => option.messageDescriptor !== undefined;

export const getLabelledUserSettingOptions = (userSetting: UserSettingKey): LabelledUserSettingOption[] =>
  getUserSettingsOptions(userSetting).filter(isLabelledOption);
